/**
 * useExportSession — the export dialog owns ONE CaptureSession for its lifetime.
 *
 * Both the live preview and the final Download/Save-to-Drive render against the
 * same settled iframe, so:
 *   - opening the dialog builds the capture once (not per preview + per click);
 *   - dragging the capture-width slider only reflows (no re-parse);
 *   - Download-then-Save reuses a cached blob instead of rendering twice.
 *
 * The hook produces a low-res `sourceCanvas` (+ optional `previewBg`) the dialog
 * draws its thumbnail from, exposes `bgInfo`/`warnings`/break rects, and an
 * `exportBlob(options)` that caches by normalized options.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { createCaptureSession, rgbToHex, type CaptureSession, type CaptureWarning } from '../client/export-capture.js'
import { rasterize } from '../client/rasterizer/index.js'
import { generateExportBlob, type ExportResult } from '../client/htmlToPdf.js'
import { exportSessionT0, markExportStage, reportExportError, reportPreviewReady } from '../client/export-errors.js'
import type { PdfExportOptions } from './PdfExportDialog.js'

// The thumbnail box is ~160px wide, so a low source scale is still ~2× the
// display size (crisp) while cutting the preview canvas' draw cost and memory —
// on a tall document a 0.5 scale allocated a multi-thousand-px-tall canvas.
// Exported: the dialog slices the sourceCanvas with this same factor.
export const PREVIEW_SCALE = 0.3

export interface ExportSessionState {
  status: 'settling' | 'ready' | 'error'
  sourceCanvas: HTMLCanvasElement | null
  previewBg: HTMLCanvasElement | null
  autoBgColor: string
  contentHeight: number
  breakRects: { top: number; bottom: number }[]
  warnings: CaptureWarning[]
  retry: () => void
  exportBlob: (options: PdfExportOptions, onProgress?: (page: number, total: number) => void) => Promise<ExportResult>
}

export function useExportSession(
  html: string,
  parentId: string | null,
  fileId: string,
  snapshotFallback: boolean,
  captureWidth: number,       // debounced
  bgFill: PdfExportOptions['bgFill'],
  enabled: boolean,           // false while the snapshot is still being prepared
): ExportSessionState {
  const [status, setStatus] = useState<'settling' | 'ready' | 'error'>('settling')
  const [sourceCanvas, setSourceCanvas] = useState<HTMLCanvasElement | null>(null)
  const [previewBg, setPreviewBg] = useState<HTMLCanvasElement | null>(null)
  const [autoBgColor, setAutoBgColor] = useState('#000000')
  const [contentHeight, setContentHeight] = useState(0)
  const [warnings, setWarnings] = useState<CaptureWarning[]>([])
  const [retryNonce, setRetryNonce] = useState(0)
  // Pagination data — computed AFTER the thumbnail shows (state, not a ref, so
  // the deferred update refreshes the page count).
  const [breakRects, setBreakRects] = useState<{ top: number; bottom: number }[]>([])

  const sessionRef = useRef<CaptureSession | null>(null)
  // Single-entry cache: its stated purpose is only "Download-then-Save doesn't
  // render twice" — a map keyed by options JSON kept every multi-MB blob a user
  // ever produced alive for the dialog's lifetime.
  const blobCache = useRef<{ key: string; p: Promise<ExportResult> } | null>(null)
  // Latest DESIRED values — the session is reconciled against these after it
  // resolves, so a width/bgFill change made while createCaptureSession is still
  // settling isn't silently dropped (the resize/bgFill effects no-op while
  // sessionRef is null).
  const desiredWidthRef = useRef(captureWidth)
  const bgFillRef = useRef(bgFill)
  bgFillRef.current = bgFill
  // Monotonic render generation: refreshPreview only commits state if no newer
  // run started meanwhile — overlapping runs (width vs bgFill vs late-settle)
  // could otherwise resolve out of order and a stale raster would win.
  const renderGenRef = useRef(0)

  // (Re)build the session when the content identity changes or on retry.
  useEffect(() => {
    if (!enabled) return
    let cancelled = false
    setStatus('settling')
    blobCache.current = null

    let created: CaptureSession | null = null
    const t0 = performance.now()
    markExportStage('pre', t0 - exportSessionT0())
    createCaptureSession(html, { parentId, width: captureWidth, snapshotFallback })
      .then(async (session) => {
        if (cancelled) { session.dispose(); return }
        created = session
        sessionRef.current = session
        // Reconcile a width change that landed during session creation.
        if (session.width() !== desiredWidthRef.current) {
          await session.setWidth(desiredWidthRef.current)
        }
        const tSession = performance.now() - t0
        await refreshPreview(session)
        // Diagnosability breadcrumb: where did the preview's time actually go?
        reportPreviewReady(fileId, {
          sessionMs: tSession,
          rasterMs: performance.now() - t0 - tSession,
          totalMs: performance.now() - t0,
        })
        // Slow assets past the preview gate: the session remeasured itself
        // once they settled — re-render so the thumbnail reflects them.
        session.settled.then((late) => {
          if (cancelled || sessionRef.current !== session || !late) return
          return refreshPreview(session)
        }).catch(() => {})
      })
      .catch((e) => {
        if (cancelled) return
        setStatus('error')
        reportExportError('capture', e, { fileId, dims: { captureWidth } })
      })

    return () => {
      cancelled = true
      if (created) created.dispose()
      if (sessionRef.current === created) sessionRef.current = null
    }
    // Rebuild only on content/retry/enable — width changes are handled by the resize effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [html, parentId, fileId, snapshotFallback, retryNonce, enabled])

  // Width changes: reflow the existing session (no re-parse), then re-preview.
  // If the session is still being created, desiredWidthRef carries the change —
  // the build effect applies it right after creation.
  useEffect(() => {
    desiredWidthRef.current = captureWidth
    const session = sessionRef.current
    if (!session) return
    let cancelled = false
    setStatus('settling')
    blobCache.current = null
    session.setWidth(captureWidth)
      .then(() => { if (!cancelled) return refreshPreview(session) })
      .catch((e) => { if (!cancelled) { setStatus('error'); reportExportError('preview', e, { fileId, dims: { captureWidth } }) } })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [captureWidth])

  // Re-render the preview raster when bgFill changes (extend strips content bg).
  useEffect(() => {
    const session = sessionRef.current
    if (!session) return
    let cancelled = false
    refreshPreview(session).catch(() => { if (!cancelled) setStatus('error') })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bgFill])

  const refreshPreview = useCallback(async (session: CaptureSession) => {
    // Read bgFill through the ref so a run started by the build/late-settle
    // paths renders with the CURRENT value, not the one captured when the
    // enclosing effect ran.
    const bgFillNow = bgFillRef.current
    const gen = ++renderGenRef.current
    const h = session.contentHeight()
    const w = session.width()
    const bg = session.bgInfo()

    // ── 1. Render the thumbnail FIRST — this is all the preview image needs.
    // The text-run + break-rect DOM walks (collectBreakRects reads
    // getComputedStyle on every element) are pagination data, NOT part of the
    // image, and the export recomputes its own break rects anyway — so they're
    // deferred below to keep them off the thumbnail's critical path.
    let bgCanvas: HTMLCanvasElement | null = null
    if ((bgFillNow === 'extend' || bgFillNow === 'tile') && bg.hasImage) {
      const tBg = performance.now()
      const layer = await session.mountBgLayer(h)
      try {
        const bgSource = await rasterize({ el: layer.el, widthCss: w, heightCss: h }, { fileId })
        bgCanvas = await bgSource.toCanvas({ x: 0, y: 0, w, h }, PREVIEW_SCALE)
        bgSource.dispose()
      } finally {
        layer.remove()
      }
      markExportStage('bg', performance.now() - tBg)
    }

    // Hold the bg-strip across capture AND draw (engines capture at different
    // moments — snapdom at rasterize(), html2canvas at toCanvas()).
    const renderCanvas = async () => {
      const source = await rasterize({ el: session.doc.documentElement, widthCss: w, heightCss: h }, { fileId })
      try {
        return await source.toCanvas({ x: 0, y: 0, w, h }, PREVIEW_SCALE)
      } finally {
        source.dispose()
      }
    }
    const tMain = performance.now()
    const canvas = bgFillNow === 'extend' && bgCanvas
      ? await session.withBgStripped(renderCanvas)
      : await renderCanvas()
    markExportStage('main', performance.now() - tMain)

    // A newer run started (or the session was superseded) while this one was
    // rendering — its raster is stale; let the newer run own the state.
    if (renderGenRef.current !== gen || sessionRef.current !== session) return

    setSourceCanvas(canvas)
    setPreviewBg(bgCanvas)
    setContentHeight(h)
    setAutoBgColor(rgbToHex(bg.color[0], bg.color[1], bg.color[2]))
    setWarnings([...session.warnings])
    setStatus('ready') // ← thumbnail is now visible

    // ── 2. Pagination data, deferred. Yield first so the thumbnail paints,
    // then compute breaks (a slightly-coarse page count shows until this lands).
    // Guard against a superseded run/session overwriting the current one.
    await new Promise<void>((r) => setTimeout(r))
    if (renderGenRef.current !== gen || sessionRef.current !== session) return
    const runs = session.collectTextRuns()
    setBreakRects(session.collectBreakRects(runs))
  }, [fileId])

  const exportBlob = useCallback((options: PdfExportOptions, onProgress?: (page: number, total: number) => void) => {
    const session = sessionRef.current
    if (!session) return Promise.reject(new Error('export session not ready'))
    const key = JSON.stringify(options)
    if (blobCache.current?.key === key) return blobCache.current.p
    const p = generateExportBlob(session, options, { fileId }, { onProgress })
    blobCache.current = { key, p }
    // Evict on failure so a retry re-renders instead of replaying the rejection.
    p.catch(() => { if (blobCache.current?.key === key) blobCache.current = null })
    return p
  }, [fileId])

  const retry = useCallback(() => setRetryNonce((n) => n + 1), [])

  return {
    status, sourceCanvas, previewBg, autoBgColor,
    contentHeight, breakRects, warnings, retry, exportBlob,
  }
}
